/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {FeatureFlag} from './FeatureFlags';

// the `features` parameter of a raw query string, with or without a value ...
const FEATURES_PARAM = /(^\?|&)features(?:=([^&]*))?(?=&|$)/;

// a path on this origin: a slash which does not begin `//`, as a browser
// reads what follows `//` as another host ...
const LOCAL_PATH = /^\/(?!\/)/;

/**
 * Puts the `dls` Feature Flag on the `features` parameter of a raw query
 * string, editing the string only so every other parameter keeps its
 * encoding.
 *
 * ```typescript
 *
 *   import {addFlagToSearch} from '@/security/featureFlagUrl';
 *   ...
 *
 *   addFlagToSearch('?a=%2F&features=apm'); // '?a=%2F&features=apm,dls'
 *
 * ```
 *
 * @param   search The raw query string, with its `?`, or an empty string.
 * @returns The query string with `dls` on the `features` parameter, which is
 *          added when the query string has none.
 */
export const addFlagToSearch = (search: string): string => {
  let result: string;
  if (FEATURES_PARAM.test(search)) {
    result = search.replace(FEATURES_PARAM, function(match: string, prefix: string, value?: string) {
      const features = value ? `${value},${FeatureFlag.DLS}` : FeatureFlag.DLS;
      return `${prefix}features=${features}`;
    });
  } else {
    const joiner = search ? '&' : '?';
    result = `${search}${joiner}features=${FeatureFlag.DLS}`;
  }
  return result;
};

/**
 * Determines if a path stays on this origin - it begins with a single slash,
 * as a path which begins with `//` would be read by the browser as another
 * host, were it given as an address.
 *
 * ```typescript
 *
 *   import {isLocalPath} from '@/security/featureFlagUrl';
 *   ...
 *
 *   isLocalPath('/login');         // true
 *   isLocalPath('//evil.com/x');   // false
 *
 * ```
 *
 * @param   path The path of the address, as `location.pathname` gives it.
 * @returns `true` when the path begins with a single slash, otherwise
 *          `false`.
 */
export const isLocalPath = (path: string): boolean => LOCAL_PATH.test(path);
